import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import {
  generateTableReportHTML,
  generateVerificationCardsHTML,
  generateSplitTableReportHTML,
  generateSplitVerificationCardsHTML,
} from './parts/studentPrintTemplates';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

// Shape returned by GET /students — one row per student_session, joined
// with sp_students + sp_users server-side (see StudentController.GetStudents).
// `id` here is the *student* identity id (student_id, a Mongo ObjectID) —
// not the student_session row id — that's what GetStudentByID /
// UpdateStudent / SwapProfilePictures all expect, so it's the id we
// standardize on for navigation/actions.
// `sp_student_id` is the human-readable school-assigned id (see
// helpers.GenerateSPStudentID) and is what gets encoded into the barcode
// on printed cards/reports — requires GetStudents to project it (see
// backend note below).
interface StudentData {
  id: string;
  sp_student_id: string;
  user_id: string;
  name: string;
  gender: string;
  email_id: string;
  class_id: string;
  section: string | null;
  admission_no: string;
  roll_no: number;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  created_at: string;
  updated_at: string;
  profile_picture_url: string;
  class_name: string;
}

interface GenderCounts {
  Male: number;
  Female: number;
  Other: number;
  total: number;
}

// Raw row shape coming back from the Go API's aggregation pipeline
// (student_sessions → sp_students → sp_users). Only the fields this
// component actually consumes are typed here.
interface RawStudentSessionRow {
  student_id: string;
  sp_student_id: string;
  user_id: string;
  class_id: string;
  section: string | null;
  roll_no: number;
  name: string;
  gender: string;
  email_id: string;
  admission_no: string;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  created_at: string;
  updated_at: string;
  profile_picture?: { url?: string } | null;
}

interface StudentsListResponse {
  status: string;
  message?: string;
  data: RawStudentSessionRow[];
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
  };
}

const PAGE_LIMIT = 100; // matches the API's max `limit`

const computeGenderCounts = (list: StudentData[]): GenderCounts => {
  const counts: GenderCounts = { Male: 0, Female: 0, Other: 0, total: list.length };
  list.forEach((s) => {
    if (s.gender === 'Male') counts.Male += 1;
    else if (s.gender === 'Female') counts.Female += 1;
    else counts.Other += 1;
  });
  return counts;
};

const StudentPDFDownloader: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Same source of truth StudentsListDashboard.tsx uses. GetStudents is
  // session-scoped server-side, so this MUST be sent — class_id alone
  // does not disambiguate which session's roll for a class you're
  // looking at once a school has more than one session on record.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [genderCounts, setGenderCounts] = useState<GenderCounts | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  useEffect(() => {
    if (selectedClass && shouldFetchStudents()) {
      fetchStudents();
    } else {
      setStudents([]);
      setGenderCounts(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, currentSessionId]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => setClasses(loadedClasses);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classes.find((c) => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const shouldFetchStudents = (): boolean => {
    if (!selectedClass) return false;
    return selectedClassHasSections() ? selectedSection !== null : true;
  };

  // GET /students — see StudentController.GetStudents (Go/Gin backend).
  //
  // Params used here:
  //   session_id  required — resolved from useSchoolDetailsStore's
  //               current_session, same as StudentsListDashboard.tsx.
  //               Without this the API can't tell which session's
  //               enrollment to scope a class_id against on multi-session
  //               schools.
  //   class_id    required for this view — scopes to one class
  //   section     only sent when the class has sections and one is selected
  //   status      "active" — this view only ever wants active students
  //   page/limit  the API caps limit at 100, so multi-page classes are
  //               paged through here until every row is collected
  //
  // Not sent: `school_id`. That's resolved from the caller's JWT
  // (ResolveSchoolIDFromRequest) for every non-superadmin role.
  //
  // The API has no `gender_counts` in this response (that's specific to
  // the old PHP endpoint) — counts are derived client-side from the rows
  // once every page has been fetched.
  //
  // Requires a backend change: GetStudents' $addFields stage does not
  // currently project sp_student_id (only GetStudentByID does). Add
  //   "sp_student_id": "$student_info.sp_student_id"
  // alongside the other $student_info.* fields in
  // StudentController.GetStudents (adminControllers/students.go), or
  // row.sp_student_id below will come back undefined and the barcode
  // will render blank.
  const fetchStudents = async (): Promise<void> => {
    if (!currentSessionId) {
      // School details (and its current_session) haven't loaded yet —
      // nothing to fetch until that's available.
      setStudents([]);
      setGenderCounts(null);
      return;
    }
    if (!selectedClass || !shouldFetchStudents()) {
      setStudents([]);
      setGenderCounts(null);
      return;
    }

    setLoading(true);
    try {
      const className = classes.find((c) => c.id === selectedClass)?.class_name || '';
      const collected: StudentData[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const params: Record<string, string | number> = {
          session_id: currentSessionId,
          class_id: selectedClass,
          status: 'active',
          page,
          limit: PAGE_LIMIT,
        };
        if (selectedClassHasSections() && selectedSection) {
          params.section = selectedSection;
        }

        const response = await apiClient.get<StudentsListResponse>('/students', { params });
        const data = response.data;

        if (data.status !== 'success') {
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: data.message || 'Failed to fetch students', life: 4000
          });
          break;
        }

        const mapped: StudentData[] = (data.data || []).map((row) => ({
          id: row.student_id,
          sp_student_id: row.sp_student_id,
          user_id: row.user_id,
          name: row.name,
          gender: row.gender,
          email_id: row.email_id,
          class_id: row.class_id,
          section: row.section,
          admission_no: row.admission_no,
          roll_no: row.roll_no,
          dob: row.dob,
          blood_group: row.blood_group,
          father_name: row.father_name,
          mother_name: row.mother_name,
          father_no: row.father_no,
          mother_no: row.mother_no,
          address: row.address,
          created_at: row.created_at,
          updated_at: row.updated_at,
          profile_picture_url: row.profile_picture?.url || '',
          class_name: className,
        }));

        collected.push(...mapped);
        totalPages = data.pagination?.total_pages ?? 1;
        page += 1;
      } while (page <= totalPages);

      setStudents(collected);
      setGenderCounts(computeGenderCounts(collected));
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching students', life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const openPrintWindow = (html: string, successMsg: string) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 500);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: successMsg, life: 4000 });
    } else {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: 'Unable to open print window. Please check your browser settings.', life: 5000
      });
    }
  };

  const guardNoStudents = (filtered?: StudentData[]): boolean => {
    const list = filtered ?? students;
    if (list.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'No Students', detail: 'No students available to generate report', life: 4000 });
      return true;
    }
    return false;
  };

  // ── All students ──────────────────────────────────────────────────────────
  const generateTableReport = () => {
    if (guardNoStudents()) return;
    const html = generateTableReportHTML(students, classes, selectedClass, selectedSection, genderCounts);
    openPrintWindow(html, `Table report generated for ${students.length} students`);
  };

  const generateVerificationCards = () => {
    if (guardNoStudents()) return;
    const html = generateVerificationCardsHTML(students, classes, selectedClass, selectedSection, genderCounts);
    openPrintWindow(html, `Verification cards generated for ${students.length} students`);
  };

  // ── Split (Male + Female sections in one PDF) ─────────────────────────────
  const generateSplitTableReport = () => {
    if (guardNoStudents()) return;
    const html = generateSplitTableReportHTML(students, classes, selectedClass, selectedSection, genderCounts);
    openPrintWindow(html, `Split table report generated for ${students.length} students`);
  };

  const generateSplitVerificationCards = () => {
    if (guardNoStudents()) return;
    const html = generateSplitVerificationCardsHTML(students, classes, selectedClass, selectedSection, genderCounts);
    openPrintWindow(html, `Split verification cards generated for ${students.length} students`);
  };

  const hasStudents = students.length > 0 && !loading;
  const hasBoth = students.some(s => s.gender === 'Male') && students.some(s => s.gender === 'Female') && !loading;

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <GlobalHeaderTitle
          title="Student Records Print Generator"
          subtitle="Generate printable student records — table format or verification cards"
          icon="pi pi-file-pdf"
          buttons={[
            {
              label: "Table Report",
              icon: "pi pi-table",
              onClick: generateTableReport,
              disabled: !hasStudents,
              style: hasStudents ? { background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' } : undefined
            },
            {
              label: "Verification Cards",
              icon: "pi pi-id-card",
              onClick: generateVerificationCards,
              disabled: !hasStudents,
              style: hasStudents ? { background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' } : undefined
            },
            {
              label: "♂♀ Split Table",
              icon: "pi pi-table",
              onClick: generateSplitTableReport,
              disabled: !hasBoth,
              style: hasBoth ? { background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' } : undefined
            },
            {
              label: "♂♀ Split Cards",
              icon: "pi pi-id-card",
              onClick: generateSplitVerificationCards,
              disabled: !hasBoth,
              style: hasBoth ? { background: 'linear-gradient(135deg, #0d2a4e 0%, #1a5faa 100%)' } : undefined
            },
          ]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={setSelectedClass}
          onSectionChange={setSelectedSection}
          onClassesLoaded={handleClassesLoaded}
        />

        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div style={{
            background: 'white', borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%'
          }}>
            {students.length > 0 ? (
              <>
                {/* Info Bar */}
                <div style={{
                  padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(5,150,105,0.1) 0%, rgba(16,185,129,0.08) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="pi pi-users" style={{ fontSize: '14px', color: '#059669' }}></i>
                    </div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                      {students.length} student{students.length !== 1 ? 's' : ''} ready for report
                    </span>
                  </div>
                  {genderCounts && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {[
                        { label: `♂ ${genderCounts.Male}`, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.15)' },
                        { label: `♀ ${genderCounts.Female}`, color: '#ec4899', bg: 'rgba(236,72,153,0.1)', border: 'rgba(236,72,153,0.15)' },
                        ...(genderCounts.Other > 0 ? [{ label: `⚥ ${genderCounts.Other}`, color: '#a855f7', bg: 'rgba(168,85,247,0.1)', border: 'rgba(168,85,247,0.15)' }] : [])
                      ].map((item) => (
                        <div key={item.label} style={{
                          padding: '6px 12px', borderRadius: '8px',
                          background: item.bg, border: `1px solid ${item.border}`
                        }}>
                          <span style={{ fontSize: '12px', color: item.color, fontWeight: '600' }}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Students Grid Preview */}
                <div style={{
                  padding: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '16px', maxHeight: '600px', overflowY: 'auto'
                }}>
                  {students.map((student) => (
                    <div key={student.id} style={{
                      border: '1.5px solid #e5e7eb', borderRadius: '12px',
                      padding: '16px', background: '#fafbfc'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        {student.profile_picture_url ? (
                          <img src={student.profile_picture_url} alt={student.name} style={{
                            width: '48px', height: '48px', borderRadius: '10px',
                            objectFit: 'cover', border: '2px solid #1e40af'
                          }} />
                        ) : (
                          <div style={{
                            width: '48px', height: '48px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '18px', fontWeight: '600', color: 'white'
                          }}>
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: '600', fontSize: '16px', color: '#1f2937',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}>{student.name}</div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>Roll: {student.roll_no}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                        <div><strong>Class:</strong> {student.class_name}{student.section ? ` - ${student.section}` : ''}</div>
                        <div><strong>Gender:</strong> {student.gender}</div>
                        <div><strong>Father:</strong> {student.father_name}</div>
                        <div><strong>Contact:</strong> {student.father_no}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '60px 20px', minHeight: '400px'
              }}>
                <div style={{
                  width: '56px', height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px', boxShadow: '0 6px 18px rgba(59,130,246,0.2)'
                }}>
                  <i className={
                    !currentSessionId ? "pi pi-exclamation-circle"
                    : !selectedClass ? "pi pi-filter"
                    : selectedClassHasSections() && !selectedSection ? "pi pi-list"
                    : "pi pi-inbox"
                  } style={{ fontSize: '25px', color: 'white' }} />
                </div>
                <h3 style={{ margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: '700', marginBottom: '8px', textAlign: 'center' }}>
                  {!currentSessionId ? "No Active Session"
                    : !selectedClass ? "No Class Selected"
                    : selectedClassHasSections() && !selectedSection ? "No Section Selected"
                    : "No Students Found"}
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500', textAlign: 'center', maxWidth: '400px', lineHeight: 1.6 }}>
                  {!currentSessionId ? "Could not determine the current session for this school. Please refresh and try again."
                    : !selectedClass ? "Please select a class from the filter above to load students"
                    : selectedClassHasSections() && !selectedSection ? "Please select a section to load students for this class"
                    : "There are no students enrolled in this class/section yet"}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default StudentPDFDownloader;